import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useApplicant } from "@/hooks/useApplicant";
import { useFormResponses } from "@/hooks/useFormResponses";
import { Field, TextInput } from "@/components/Field";
import type { UpdatePersonRequest } from "@/lib/api-types";
import {
  combineFieldErrors,
  combineFormError,
  getFieldErrors,
  getFormError,
  runUpserts,
} from "@/lib/validation-errors";

/**
 * Step 8 — Contact information & signature date (Part 11 of N-400).
 *
 * Captures the contact details USCIS prints on Part 11 alongside the
 * applicant's typed signature. Phone (daytime) and email live on the
 * Person row; mobile and signature_date are form_responses (no
 * dedicated columns, low-value to migrate to schema).
 *
 * The signature date is "today, the day you intend to file" — we
 * default it to today on first load so most applicants don't have to
 * touch it.
 */

const FR_KEYS = {
  // daytime_phone overrides Person.Phone in the mapping if both set;
  // we capture it as a form_response only when the user wants the
  // form to print a different number than their account-level phone.
  daytimePhone: "daytime_phone",
  mobilePhone: "mobile_phone",
  signatureDate: "signature_date",
} as const;

function todayISO(): string {
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
}

export function StepContact() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { applicant, isLoading, update, error } = useApplicant(id);
  const fr = useFormResponses(id);

  // Person fields
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  // form_response fields
  const [mobile, setMobile] = useState("");
  const [signatureDate, setSignatureDate] = useState("");

  // Hydrate Person fields when applicant loads.
  useEffect(() => {
    if (!applicant) return;
    setPhone(applicant.phone ?? "");
    setEmail(applicant.email ?? "");
  }, [applicant]);

  // Hydrate form_responses; default signatureDate to today if unset.
  useEffect(() => {
    if (fr.isLoading) return;
    setMobile(fr.getString(FR_KEYS.mobilePhone));
    const sigStored = fr.getString(FR_KEYS.signatureDate);
    setSignatureDate(sigStored || todayISO());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fr.isLoading, fr.get(FR_KEYS.mobilePhone), fr.get(FR_KEYS.signatureDate)]);

  const [saveErrors, setSaveErrors] = useState<unknown[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!applicant) return;
    setSaveErrors([]);

    // Person fields — phone + email both go on the Person row. We treat
    // these as the "daytime phone" and "email address" the form uses.
    const patch: UpdatePersonRequest = {};
    if (phone) patch.phone = phone;
    if (email) patch.email = email;
    if (Object.keys(patch).length > 0) {
      try {
        await update.mutateAsync(patch);
      } catch {
        // update.error carries the failure — fieldErrors below will
        // surface it. Bail before the form_response upserts.
        return;
      }
    }

    // form_responses
    const upserts: Promise<unknown>[] = [];
    if (mobile) {
      upserts.push(fr.upsert.mutateAsync({ fieldKey: FR_KEYS.mobilePhone, value: mobile }));
    }
    if (signatureDate) {
      upserts.push(
        fr.upsert.mutateAsync({ fieldKey: FR_KEYS.signatureDate, value: signatureDate }),
      );
    }
    const errors = await runUpserts(upserts);
    if (errors.length > 0) {
      setSaveErrors(errors);
      return;
    }

    navigate(`/cases/${id}/wizard/fee-reduction`);
  };

  // Person PATCH errors come from update.error (per-field below);
  // form_response upsert errors are accumulated in saveErrors so a
  // failing upsert isn't silently overwritten by a later successful
  // one (Promise.all + shared react-query mutation race).
  const fieldErrors = getFieldErrors(update.error);
  const personFormError = getFormError(update.error);
  const frFormError = combineFormError(saveErrors);
  const frFieldErrors = combineFieldErrors(saveErrors);

  if (isLoading) return <div className="text-slate-500">Loading…</div>;
  if (error) {
    return (
      <div className="text-red-600 text-sm">
        Could not load your application: {(error as Error).message}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Contact & signature</h1>
        <p className="text-sm text-slate-500 mt-1">
          USCIS prints these on Part 11 of the form. The signature is
          your typed full name — same as on your Permanent Resident Card.
        </p>
      </header>

      <fieldset className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <legend className="font-medium text-slate-900 px-1">How USCIS reaches you</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Daytime telephone"
            required
            hint="A number where USCIS can reach you between 9am and 5pm."
            error={fieldErrors?.["phone"]?.message}
          >
            <TextInput
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="tel"
              placeholder="+1 555 123 4567"
            />
          </Field>
          <Field
            label="Mobile telephone"
            hint="Optional — leave blank if you only have one number."
            error={frFieldErrors?.[FR_KEYS.mobilePhone]?.message}
          >
            <TextInput
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              autoComplete="tel"
              placeholder="+1 555 987 6543"
            />
          </Field>
          <Field
            label="Email address"
            required
            hint="Used for case-status updates from USCIS."
            className="sm:col-span-2"
            error={fieldErrors?.["email"]?.message}
          >
            <TextInput
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </Field>
        </div>
      </fieldset>

      <fieldset className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <legend className="font-medium text-slate-900 px-1">Signature date</legend>
        <p className="text-sm text-slate-500">
          The date you intend to sign and submit Form N-400. Defaults
          to today; change it only if you plan to wait before mailing
          your packet.
        </p>
        <Field
          label="Date of signature"
          required
          error={frFieldErrors?.[FR_KEYS.signatureDate]?.message}
        >
          <TextInput
            type="date"
            value={signatureDate}
            onChange={(e) => setSignatureDate(e.target.value)}
          />
        </Field>
      </fieldset>

      {personFormError && (
        <p className="text-sm text-red-600">Could not save: {personFormError}</p>
      )}
      {frFormError && (
        <p className="text-sm text-red-600">Could not save: {frFormError}</p>
      )}
      {(fieldErrors || frFieldErrors) && (
        <p className="text-xs text-red-600">
          Some fields above need attention before continuing.
        </p>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
        <Link
          to={`/cases/${id}/wizard/oath`}
          className="text-sm text-slate-600 hover:text-slate-900"
        >
          ← Oath
        </Link>
        <button
          type="submit"
          disabled={update.isPending || fr.upsert.isPending}
          className="rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300"
        >
          {update.isPending || fr.upsert.isPending ? "Saving…" : "Save & continue"}
        </button>
      </div>
    </form>
  );
}
